import { GridColDef } from '@mui/x-data-grid-premium';
import { CustomDataGrid, StackedTextCell } from '@retail/components';
import { useMemo } from 'react';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { PricePerUnit } from '@retail/products/components';
import { getLLDate } from '@retail/utils';
import { useRowColorGrouping } from '@retail/hooks';
import { AssortmentPrices } from '@retail/my-assortment/types';

interface Props {
  items: AssortmentPrices[];
  loading: boolean;
}

const baseColumnProps: Partial<GridColDef> = {
  width: 140,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
};

export function AffectedItemsTable({ items, loading }: Props) {
  const t = useCalculusTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<AssortmentPrices>[] = useMemo(
    () => [
      {
        field: 'primaryText',
        width: 280,
        headerName: t('calculus.itemsAffected.columns.itemName'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.primaryText} bottomLine={row.secondaryText} />
        ),
      },
      {
        field: 'mgItemAndFinfo',
        width: 160,
        headerName: t('calculus.itemsAffected.columns.mgItemAndFinfo'),
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.mgItemNumber} bottomLine={row.finfoNumber} />
        ),
      },
      {
        field: 'productGroup',
        ...baseColumnProps,
        width: 220,
        headerName: t('calculus.itemsAffected.columns.productGroup'),
        renderCell: ({ row }) => (
          <StackedTextCell
            topLine={row.productGroup.name}
            bottomLine={row.productGroup.groupNumber}
          />
        ),
      },
      {
        field: 'purchasePrice',
        ...baseColumnProps,
        headerName: t('calculus.prices.purchasePrice'),
        renderCell: ({ row }) => {
          const purchasePrice = row.pricesExVat.purchasePrice;
          const priceDate = getLLDate(row.purchasePriceDate);
          const primaryUnit = row.primaryUnit;

          return purchasePrice ? (
            <StackedTextCell
              nullableBottomLine
              centerAlign
              topLine={<PricePerUnit price={purchasePrice} unit={primaryUnit} />}
              bottomLine={priceDate}
            />
          ) : (
            '-'
          );
        },
      },
      {
        field: 'costPrice',
        ...baseColumnProps,
        headerName: t('calculus.prices.costPrice'),
        renderCell: ({ row }) => {
          const costPrice = row.pricesExVat.costPrice;
          const priceDate = getLLDate(row.costPriceDate);
          const primaryUnit = row.primaryUnit;

          return costPrice ? (
            <StackedTextCell
              nullableBottomLine
              centerAlign
              topLine={<PricePerUnit price={costPrice} unit={primaryUnit} />}
              bottomLine={priceDate}
            />
          ) : (
            '-'
          );
        },
      },
      {
        field: 'salesPrice',
        ...baseColumnProps,
        headerName: t('calculus.prices.salesPrice'),
        renderCell: ({ row }) => {
          const salesPrice = row.pricesIncVat.salesPrice;
          const priceDate = getLLDate(row.salesPriceDate);
          const primaryUnit = row.primaryUnit;

          return salesPrice ? (
            <StackedTextCell
              nullableBottomLine
              centerAlign
              topLine={<PricePerUnit price={salesPrice} unit={primaryUnit} />}
              bottomLine={priceDate}
            />
          ) : (
            '-'
          );
        },
      },
    ],
    [t]
  );

  return (
    <CustomDataGrid
      disableRowSelectionOnClick
      getRowClassName={getRowColorForStripedTable}
      rowHeight={65}
      loading={loading}
      rows={items}
      columns={columns}
      getRowId={(row) => row.mgItemNumber + row.finfoNumber}
    />
  );
}
